<template>
  <div class="content">
    <div class="analytics-wrap">
      <card class="card no-border-card" no-body>
        <div class="glad-analytics">

          <header class="glad-analytics__header">
            <div class="glad-analytics__header-text">
              <p class="glad-eyebrow">{{ eyebrow }}</p>
              <h1 class="glad-analytics__title">{{ pageTitle }}</h1>
              <p class="glad-analytics__sub">{{ pageSub }}</p>
            </div>
            <button
              v-if="!loading && !error"
              type="button"
              class="glad-export-btn"
              @click="exportCsv">
              <span aria-hidden="true">⬇</span> Export CSV
            </button>
          </header>

          <div v-if="loading" class="glad-loading">
            <span class="glad-spinner"></span>
          </div>

          <div v-else-if="error" class="glad-error">
            {{ error }}
          </div>

          <template v-else>
            <!-- KPI cards -->
            <section class="glad-kpi-grid">
              <article
                v-for="kpi in kpiCards"
                :key="kpi.key"
                class="glad-kpi">
                <p class="glad-kpi__label">{{ kpi.label }}</p>
                <p class="glad-kpi__value">{{ kpi.value }}</p>
                <p v-if="kpi.hint" class="glad-kpi__hint">{{ kpi.hint }}</p>
              </article>
            </section>

            <!-- Pathway breakdown -->
            <section class="glad-section">
              <header class="glad-section__head">
                <h2 class="glad-section__heading">Pathways</h2>
                <span class="glad-section__hint">Enrollment and completion by pathway</span>
              </header>

              <div v-if="!data.pathways.length" class="glad-empty">
                No pathways published yet.
              </div>

              <ul v-else class="glad-pathway-list">
                <li
                  v-for="p in data.pathways"
                  :key="p.id"
                  class="glad-pathway-row">
                  <div class="glad-pathway-row__meta">
                    <h3 class="glad-pathway-row__title">{{ p.title }}</h3>
                    <p class="glad-pathway-row__sub">
                      {{ p.enrolled }} enrolled · {{ p.completed }} completed
                    </p>
                  </div>
                  <div class="glad-pathway-row__bar-wrap">
                    <div class="glad-pathway-row__bar">
                      <div
                        class="glad-pathway-row__bar-fill"
                        :style="{ width: p.completion_rate + '%' }"
                        :aria-label="p.completion_rate + ' percent completion'">
                      </div>
                    </div>
                    <span class="glad-pathway-row__pct">{{ p.completion_rate }}%</span>
                  </div>
                </li>
              </ul>
            </section>

            <!-- RTC breakdown (NTC scope only) -->
            <section v-if="data.rtcs" class="glad-section">
              <header class="glad-section__head">
                <h2 class="glad-section__heading">Regional Training Centers</h2>
                <span class="glad-section__hint">Select an RTC to view its learners</span>
              </header>

              <div class="glad-table-wrap">
                <table class="glad-table glad-table--rtc">
                  <thead>
                    <tr>
                      <th scope="col">RTC</th>
                      <th scope="col" class="glad-table__num">Learners</th>
                      <th scope="col" class="glad-table__num">Enrolled</th>
                      <th scope="col" class="glad-table__num">Pathways done</th>
                      <th scope="col" class="glad-table__num">Badges</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="r in data.rtcs">
                      <tr
                        :key="r.id"
                        class="glad-rtc-row"
                        :class="{ 'glad-rtc-row--open': isExpanded(r.id) }"
                        role="button"
                        :aria-expanded="isExpanded(r.id) ? 'true' : 'false'"
                        @click="toggleRtc(r)">
                        <td>
                          <span class="glad-rtc-caret" :class="{ 'glad-rtc-caret--open': isExpanded(r.id) }" aria-hidden="true">▸</span>
                          <span class="glad-rtc-name">{{ r.name }}</span>
                          <span v-if="r.region" class="glad-rtc-region">{{ r.region }}</span>
                          <span v-if="r.is_ntc" class="glad-rtc-ntc">NTC</span>
                        </td>
                        <td class="glad-table__num">{{ r.learners }}</td>
                        <td class="glad-table__num">{{ r.enrolled }}</td>
                        <td class="glad-table__num">{{ r.completed }}</td>
                        <td class="glad-table__num">{{ r.badges }}</td>
                      </tr>

                      <tr v-if="isExpanded(r.id)" :key="r.id + '-detail'" class="glad-rtc-detail">
                        <td colspan="5">
                          <div v-if="learnersLoading[r.id]" class="glad-rtc-detail__state">
                            <span class="glad-spinner glad-spinner--sm"></span> Loading learners…
                          </div>
                          <div v-else-if="learnersError[r.id]" class="glad-rtc-detail__state glad-rtc-detail__state--error">
                            {{ learnersError[r.id] }}
                          </div>
                          <div v-else-if="!(learners[r.id] && learners[r.id].length)" class="glad-rtc-detail__state">
                            No learners assigned to this RTC yet.
                          </div>
                          <table v-else class="glad-subtable">
                            <thead>
                              <tr>
                                <th scope="col">Learner</th>
                                <th scope="col">Role</th>
                                <th scope="col">Status</th>
                                <th scope="col" class="glad-table__num">Enrolled</th>
                                <th scope="col" class="glad-table__num">Pathways done</th>
                                <th scope="col" class="glad-table__num">Modules done</th>
                                <th scope="col" class="glad-table__num">Badges</th>
                                <th scope="col">Last activity</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="l in learners[r.id]" :key="l.id">
                                <td>
                                  <span class="glad-learner-name">{{ l.name }}</span>
                                  <span class="glad-learner-email">{{ l.email }}</span>
                                </td>
                                <td>{{ l.role }}</td>
                                <td>
                                  <span class="glad-status" :class="l.is_active ? 'glad-status--active' : 'glad-status--inactive'">
                                    {{ l.is_active ? 'Active' : 'Inactive' }}
                                  </span>
                                </td>
                                <td class="glad-table__num">{{ l.pathways_enrolled }}</td>
                                <td class="glad-table__num">{{ l.pathways_completed }}</td>
                                <td class="glad-table__num">{{ l.modules_completed }}</td>
                                <td class="glad-table__num">{{ l.badges }}</td>
                                <td class="glad-learner-activity">{{ formatActivity(l.last_activity) }}</td>
                              </tr>
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </section>

            <!-- Activity feed -->
            <section class="glad-section">
              <header class="glad-section__head">
                <h2 class="glad-section__heading">Recent activity</h2>
                <span class="glad-section__hint">Most recent learner milestones in your scope</span>
              </header>

              <div v-if="!data.activity.length" class="glad-empty">
                No learner activity yet.
              </div>

              <ul v-else class="glad-activity-list">
                <li
                  v-for="(a, idx) in data.activity"
                  :key="idx"
                  class="glad-activity-row">
                  <span class="glad-activity-row__dot" :class="'glad-activity-row__dot--' + a.kind"></span>
                  <div class="glad-activity-row__body">
                    <p class="glad-activity-row__line">
                      <span class="glad-activity-row__name">{{ a.learner || 'Unknown' }}</span>
                      <span v-if="a.role" class="glad-activity-row__role">{{ a.role }}</span>
                      <span class="glad-activity-row__verb">{{ verbFor(a.kind) }}</span>
                      <strong>{{ a.subject }}</strong>
                      <span v-if="a.context" class="glad-activity-row__ctx">in {{ a.context }}</span>
                    </p>
                    <p class="glad-activity-row__when">{{ formatDate(a.when) }}</p>
                  </div>
                </li>
              </ul>
            </section>
          </template>

        </div>
      </card>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AnalyticsDashboard',
  data() {
    return {
      loading: true,
      error: '',
      data: {
        scope: null,
        totals: {},
        pathways: [],
        rtcs: null,
        activity: [],
      },
      // Expandable per-RTC learner rosters (NTC view).
      expanded: {},
      learners: {},
      learnersLoading: {},
      learnersError: {},
    };
  },
  computed: {
    isNtc() {
      return this.data.scope === 'national';
    },
    eyebrow() {
      return this.isNtc ? 'NTC Admin View' : 'RTC Admin View';
    },
    pageTitle() {
      return this.isNtc ? 'National Analytics' : 'RTC Analytics';
    },
    pageSub() {
      return this.isNtc
        ? 'Cross-RTC enrollment, completion, and credentialing metrics.'
        : 'Enrollment, completion, and credentialing within your RTC.';
    },
    kpiCards() {
      const t = this.data.totals || {};
      return [
        { key: 'learners',  label: 'Learners in scope',  value: t.learners_total ?? 0 },
        { key: 'enrolled',  label: 'Active learners',    value: t.learners_in_progress ?? 0, hint: (t.learners_enrolled ?? 0) + ' total enrolled' },
        { key: 'modules',   label: 'Modules completed',  value: t.modules_completed ?? 0 },
        { key: 'badges',    label: 'Badges issued',      value: t.badges_issued ?? 0,        hint: (t.pathways_completed ?? 0) + ' full pathways done' },
      ];
    },
  },
  created() {
    this.fetch();
  },
  methods: {
    fetch() {
      this.loading = true;
      this.error = '';
      this.$http.get('glad/analytics/overview')
        .then(resp => { this.data = resp.data; })
        .catch(err => {
          if (err && err.response && err.response.status === 403) {
            this.error = 'Analytics is admin-only. Switch to an RTC or NTC admin account to view this page.';
          } else {
            this.error = 'Could not load analytics. Please try again.';
          }
        })
        .finally(() => { this.loading = false; });
    },

    /* --- Expandable RTC learner rosters --------------------------------- */
    isExpanded(rtcId) {
      return !!this.expanded[rtcId];
    },
    toggleRtc(rtc) {
      const id = rtc.id;
      if (this.expanded[id]) {
        this.$set(this.expanded, id, false);
        return;
      }
      this.$set(this.expanded, id, true);
      if (!this.learners[id]) {
        this.fetchLearners(id);
      }
    },
    fetchLearners(rtcId) {
      this.$set(this.learnersLoading, rtcId, true);
      this.$set(this.learnersError, rtcId, '');
      this.$http.get('glad/analytics/rtcs/' + rtcId + '/learners')
        .then(resp => {
          this.$set(this.learners, rtcId, (resp.data && resp.data.data) || []);
        })
        .catch(() => {
          this.$set(this.learnersError, rtcId, 'Could not load learners for this RTC.');
        })
        .finally(() => {
          this.$set(this.learnersLoading, rtcId, false);
        });
    },
    formatActivity(d) {
      if (!d) return 'No activity';
      const normalized = /[zZ]|[+\-]\d{2}:?\d{2}$/.test(d) ? d : d.replace(' ', 'T') + 'Z';
      const parsed = new Date(normalized);
      if (isNaN(parsed.getTime())) return d;
      try {
        return parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
      } catch (e) {
        return d;
      }
    },
    verbFor(kind) {
      return {
        module_complete: 'completed module',
        module_badge:    'earned badge for',
        pathway_badge:   'earned pathway badge for',
      }[kind] || 'updated';
    },
    csvCell(v) {
      // Quote any value that could contain a comma, quote, or newline.
      const s = (v === null || v === undefined) ? '' : String(v);
      return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    },
    exportCsv() {
      const t = this.data.totals || {};
      const rows = [];
      const push = (arr) => rows.push(arr.map(this.csvCell).join(','));

      // Section 1 — summary KPIs
      push(['Project GLAD® Analytics Export']);
      push(['Scope', this.isNtc ? 'National (all RTCs)' : 'Single RTC']);
      push([]);
      push(['Summary metric', 'Value']);
      push(['Learners in scope', t.learners_total ?? 0]);
      push(['Active learners', t.learners_in_progress ?? 0]);
      push(['Total enrolled', t.learners_enrolled ?? 0]);
      push(['Modules completed', t.modules_completed ?? 0]);
      push(['Pathways completed', t.pathways_completed ?? 0]);
      push(['Badges issued', t.badges_issued ?? 0]);
      push([]);

      // Section 2 — per-pathway breakdown
      push(['Pathway', 'Enrolled', 'Completed', 'Completion rate (%)']);
      (this.data.pathways || []).forEach(p => {
        push([p.title, p.enrolled, p.completed, p.completion_rate]);
      });

      // Section 3 — per-RTC breakdown (national scope only)
      if (this.data.rtcs && this.data.rtcs.length) {
        push([]);
        push(['RTC', 'Region', 'Learners', 'Enrolled', 'Pathways completed', 'Badges']);
        this.data.rtcs.forEach(r => {
          push([r.name, r.region || (r.is_ntc ? 'National' : ''), r.learners, r.enrolled, r.completed, r.badges]);
        });
      }

      const csv = rows.join('\r\n');
      const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `glad-analytics-${this.isNtc ? 'national' : 'rtc'}-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    formatDate(d) {
      if (!d) return '';
      // Laravel returns "YYYY-MM-DD" or "YYYY-MM-DD HH:MM:SS" without TZ.
      const normalized = /[zZ]|[+\-]\d{2}:?\d{2}$/.test(d)
        ? d
        : d.replace(' ', 'T') + 'Z';
      const parsed = new Date(normalized);
      if (isNaN(parsed.getTime())) return d;
      const now = new Date();
      const diffMs = now.getTime() - parsed.getTime();
      const diffHr = Math.round(diffMs / 3600000);
      if (diffHr < 1)  return 'just now';
      if (diffHr < 24) return `${diffHr}h ago`;
      const diffDay = Math.round(diffHr / 24);
      if (diffDay < 7) return `${diffDay}d ago`;
      try {
        return parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
      } catch (e) {
        return d;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@/assets/sass/glad-tokens.scss';

.analytics-wrap { width: 100%; }
.analytics-wrap ::v-deep > .card.no-border-card {
  border: 0;
  box-shadow: none;
}
.glad-analytics {
  padding: 1.5rem 1.5rem 2.5rem;
  font-family: var(--glad-font-sans);
}

/* Header ------------------------------------------------------------------- */
.glad-analytics__header {
  margin-bottom: 1.75rem;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}
.glad-analytics__header-text { min-width: 0; }
.glad-export-btn {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.95rem;
  background: var(--glad-surface);
  border: 1px solid var(--glad-line);
  border-radius: var(--glad-radius);
  color: var(--glad-ink);
  font-family: var(--glad-font-sans);
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
  &:hover { background: var(--glad-paper-2); border-color: var(--glad-text-muted); }
}
.glad-eyebrow {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--glad-accent);
  display: block;
  margin: 0 0 0.45rem;
}
.glad-analytics__title {
  font-family: var(--glad-font-serif);
  font-size: 1.7rem;
  font-weight: 700;
  color: var(--glad-ink);
  margin: 0 0 0.35rem;
  line-height: 1.2;
}
.glad-analytics__sub {
  color: var(--glad-text-soft);
  font-size: 0.9rem;
  margin: 0;
}

/* Loading + error ---------------------------------------------------------- */
.glad-loading {
  display: flex;
  justify-content: center;
  padding: 4rem 0;
}
.glad-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid var(--glad-line);
  border-top-color: var(--glad-ink);
  border-radius: 50%;
  animation: glad-spin 0.7s linear infinite;
  display: inline-block;
}
@keyframes glad-spin { to { transform: rotate(360deg); } }

.glad-error {
  padding: 2.5rem 1rem;
  text-align: center;
  background: var(--glad-paper-2);
  border-radius: var(--glad-radius);
  color: var(--glad-text-soft);
  font-size: 0.9rem;
}

/* KPI grid ----------------------------------------------------------------- */
.glad-kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.85rem;
  margin-bottom: 2.25rem;
}
.glad-kpi {
  padding: 1.1rem 1.15rem;
  background: var(--glad-surface);
  border: 1px solid var(--glad-line);
  border-radius: var(--glad-radius-lg);
}
.glad-kpi__label {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--glad-text-soft);
  margin: 0 0 0.4rem;
}
.glad-kpi__value {
  font-family: var(--glad-font-serif);
  font-size: 1.85rem;
  font-weight: 700;
  color: var(--glad-ink);
  margin: 0;
  line-height: 1;
}
.glad-kpi__hint {
  font-size: 0.74rem;
  color: var(--glad-text-muted);
  margin: 0.45rem 0 0;
}

/* Sections shared layout --------------------------------------------------- */
.glad-section { margin-bottom: 2.25rem; }
.glad-section:last-child { margin-bottom: 0; }

.glad-section__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.6rem;
  margin-bottom: 0.85rem;
  flex-wrap: wrap;
}
.glad-section__heading {
  font-family: var(--glad-font-serif);
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--glad-ink);
  margin: 0;
}
.glad-section__hint {
  font-size: 0.78rem;
  color: var(--glad-text-muted);
}

.glad-empty {
  font-size: 0.9rem;
  color: var(--glad-text-soft);
  text-align: center;
  padding: 1.5rem 0;
}

/* Pathway list ------------------------------------------------------------- */
.glad-pathway-list { list-style: none; margin: 0; padding: 0; }
.glad-pathway-row {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  padding: 0.85rem 0;
  border-bottom: 1px solid var(--glad-line);

  &:last-child { border-bottom: none; }
}
.glad-pathway-row__meta { flex: 1; min-width: 0; }
.glad-pathway-row__title {
  font-family: var(--glad-font-serif);
  font-size: 1rem;
  font-weight: 600;
  color: var(--glad-ink);
  margin: 0 0 0.2rem;
}
.glad-pathway-row__sub {
  font-size: 0.78rem;
  color: var(--glad-text-muted);
  margin: 0;
}
.glad-pathway-row__bar-wrap {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 45%;
  min-width: 200px;
}
.glad-pathway-row__bar {
  flex: 1;
  height: 8px;
  background: var(--glad-paper-2);
  border-radius: 999px;
  overflow: hidden;
}
.glad-pathway-row__bar-fill {
  display: block;
  height: 100%;
  min-width: 2px;
  background: var(--glad-ink);
  border-radius: 999px;
  transition: width 0.3s ease;
}
.glad-pathway-row__pct {
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--glad-ink);
  min-width: 36px;
  text-align: right;
  font-variant-numeric: tabular-nums;
}

/* Table -------------------------------------------------------------------- */
.glad-table-wrap {
  border: 1px solid var(--glad-line);
  border-radius: var(--glad-radius);
  overflow: hidden;
}
.glad-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;

  th, td {
    text-align: left;
    padding: 0.65rem 0.85rem;
    border-bottom: 1px solid var(--glad-line);
  }
  tr:last-child td { border-bottom: 0; }

  thead th {
    background: var(--glad-paper);
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--glad-text-soft);
  }

  &__num {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
  thead .glad-table__num { text-align: right; }
}
.glad-rtc-name { font-weight: 600; color: var(--glad-ink); }
.glad-rtc-region {
  display: inline-block;
  margin-left: 0.45rem;
  font-size: 0.72rem;
  color: var(--glad-text-muted);
}
.glad-rtc-ntc {
  display: inline-block;
  margin-left: 0.45rem;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  padding: 0.1rem 0.4rem;
  background: rgba(15, 17, 21, 0.08);
  color: var(--glad-ink);
  border-radius: 999px;
}

/* Expandable RTC rows ------------------------------------------------------ */
.glad-table--rtc .glad-rtc-row { cursor: pointer; transition: background 0.12s ease; }
.glad-table--rtc .glad-rtc-row:hover { background: var(--glad-paper); }
.glad-table--rtc .glad-rtc-row--open { background: var(--glad-paper); }
.glad-table--rtc .glad-rtc-row--open td { border-bottom-color: transparent; }

.glad-rtc-caret {
  display: inline-block;
  width: 1em;
  margin-right: 0.4rem;
  color: var(--glad-text-muted);
  transition: transform 0.15s ease;
}
.glad-rtc-caret--open { transform: rotate(90deg); }

.glad-rtc-detail > td {
  background: var(--glad-paper-2);
  padding: 0.5rem 0.85rem 0.9rem 1.9rem;
}
.glad-rtc-detail__state {
  font-size: 0.84rem;
  color: var(--glad-text-soft);
  padding: 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.45rem;
}
.glad-rtc-detail__state--error { color: #b3261e; }

.glad-subtable {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
  background: var(--glad-surface);
  border: 1px solid var(--glad-line);
  border-radius: var(--glad-radius-sm);
  overflow: hidden;

  th, td {
    text-align: left;
    padding: 0.5rem 0.7rem;
    border-bottom: 1px solid var(--glad-line);
  }
  tr:last-child td { border-bottom: 0; }
  thead th {
    background: var(--glad-paper);
    font-size: 0.66rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--glad-text-muted);
  }
  .glad-table__num { text-align: right; font-variant-numeric: tabular-nums; }
}
.glad-learner-name { display: block; font-weight: 600; color: var(--glad-ink); }
.glad-learner-email { display: block; font-size: 0.74rem; color: var(--glad-text-muted); margin-top: 0.05rem; }
.glad-learner-activity { color: var(--glad-text-soft); white-space: nowrap; }

.glad-status {
  display: inline-block;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 0.16rem 0.5rem;
  border-radius: 999px;

  &--active { background: rgba(180, 83, 9, 0.12); color: var(--glad-accent); }
  &--inactive { background: var(--glad-paper-2); color: var(--glad-text-muted); }
}

.glad-spinner--sm {
  width: 15px;
  height: 15px;
  border-width: 2px;
}

/* Activity feed ------------------------------------------------------------ */
.glad-activity-list { list-style: none; margin: 0; padding: 0; }
.glad-activity-row {
  display: flex;
  gap: 0.75rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--glad-line);

  &:last-child { border-bottom: none; }
}
.glad-activity-row__dot {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-top: 0.55rem;
  background: var(--glad-text-muted);

  &--module_complete { background: var(--glad-text-muted); }
  &--module_badge    { background: var(--glad-accent); }
  &--pathway_badge   { background: var(--glad-ink); }
}
.glad-activity-row__body { flex: 1; min-width: 0; }
.glad-activity-row__line {
  margin: 0;
  font-size: 0.9rem;
  color: var(--glad-text-soft);
  line-height: 1.4;
  strong { color: var(--glad-ink); font-weight: 600; }
}
.glad-activity-row__name {
  color: var(--glad-ink);
  font-weight: 600;
  margin-right: 0.35rem;
}
.glad-activity-row__role {
  display: inline-block;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 0.08rem 0.45rem;
  border-radius: 999px;
  background: var(--glad-paper-2);
  color: var(--glad-text-soft);
  margin-right: 0.4rem;
}
.glad-activity-row__verb { margin-right: 0.25rem; }
.glad-activity-row__ctx {
  margin-left: 0.35rem;
  color: var(--glad-text-muted);
}
.glad-activity-row__when {
  font-size: 0.74rem;
  color: var(--glad-text-muted);
  margin: 0.25rem 0 0;
}
</style>
